import React, { useEffect } from "react";
import { Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import TextField from "@/common/elements/InputElements/TextField";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import dayjs from "dayjs";
import { getRefListNameByID } from "@/util/common_function";
import CommonDatePicker from "@/common/elements/DatePicker";
import TimePicker from "@/common/elements/TimePicker";
import { fetchGlobalCriteria } from "@/apis/security/globalCriteriaApi";
import { usePermissionMask } from "@/util/usePermissionMask";
import { usePermissionSync } from "@/util/usePermissionSync";
import PermissionGroup from "../PermissionGroup";

export default function RulesCriteriaLocksPermitsForm({ form, initialValues = {}, onFinish, disabled = false }) {
  const dispatch = useDispatch();
  useEffect(() => { dispatch(fetchGlobalCriteria()); }, [dispatch]);
  const rawCriteria = useSelector(state => state.globalCriteria.list || []);
  const criteriaOptions = rawCriteria.map(c => ({ value: c.globalCriteriaID, label: c.criteriaName }));
  const { items: permissionItems, parsePermissionMask, buildPermissionMask } = usePermissionMask();
  const onValuesChange = usePermissionSync(form, permissionItems);

  useEffect(() => {
    if (!initialValues) return;
    form.setFieldsValue({
      securityRulesCriteriaID: initialValues.securityRulesCriteriaID,
      ruleType: initialValues.ruleType || "CRITERIA",
      ruleActive: initialValues.ruleActive || true,
      globalCriteriaID: initialValues.globalCriteriaID,
      startingPoint: initialValues.startingPoint,
      comment: initialValues.comment,
      allowSetting: parsePermissionMask(initialValues.allowSetting),
      denySetting: parsePermissionMask(initialValues.denySetting),
      id: initialValues.securityRulesCriteriaID,
      dateAdded: initialValues.dateAdded ? dayjs(initialValues.dateAdded) : dayjs(),
      timeAdded: initialValues.timeAdded ? dayjs(initialValues.timeAdded, "HH:mm") : dayjs(),
      user: initialValues.user || getRefListNameByID("", JSON.parse(localStorage.getItem("userDetails"))?.staffId, true, false)
    });
  }, [initialValues, form, parsePermissionMask]);

  const handleFinish = values => onFinish({
    ...values,
    allowSetting: buildPermissionMask(values.allowSetting),
    denySetting: buildPermissionMask(values.denySetting)
  });

  return (
    <Form form={form} onFinish={handleFinish} layout="vertical" disabled={disabled} onValuesChange={onValuesChange}>
      <StyledSectionHeading><span>Rule Type and Status</span></StyledSectionHeading>
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={8}><FormItem name="ruleType" label="Rule Type"><TextField disabled /></FormItem></StyledGridCol>
        <StyledGridCol lg={8}><FormItem name="ruleActive" label="Is Rule Active?" layout="horizontal" labelPostion="label-right" paddingTop="26px" valuePropName="checked"><Checkbox /></FormItem></StyledGridCol>
      </StyledGridRow>
      <StyledSectionHeading><span>Criteria Lock Info</span></StyledSectionHeading>
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={12}><FormItem name="globalCriteriaID" label="Global Criteria"><Select options={criteriaOptions} placeholder="Select Criteria" disabled={disabled} /></FormItem></StyledGridCol>
        <StyledGridCol lg={12}><FormItem name="startingPoint" label="Starting Point"><TextField /></FormItem></StyledGridCol>
        <StyledGridCol lg={24}><FormItem name="comment" label="Comments"><TextField inputType="textarea" /></FormItem></StyledGridCol>
      </StyledGridRow>
      {permissionItems.length === 0 ? <div>Loading permissions…</div> :
        <StyledGridRow gutter={16}>
          <PermissionGroup form={form} prefix="allowSetting" heading="Allowed Settings" permissionItems={permissionItems} />
          <PermissionGroup form={form} prefix="denySetting" heading="Denied Settings" permissionItems={permissionItems} />
        </StyledGridRow>
      }
      <StyledSectionHeading marginTop="20px">
        <span>Security Rule (Criteria Lock) Administrative Information</span>
      </StyledSectionHeading>
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={2}><FormItem name="id" label="Id"><TextField disabled /></FormItem></StyledGridCol>
        <StyledGridCol lg={6}><FormItem name="dateAdded" label="Date Entered"><CommonDatePicker format="MM/DD/YYYY" disabled /></FormItem></StyledGridCol>
        <StyledGridCol lg={6}><FormItem name="timeAdded" label="Time Entered"><TimePicker format="HH:mm" disabled /></FormItem></StyledGridCol>
        <StyledGridCol lg={6}><FormItem name="user" label="User"><TextField disabled /></FormItem></StyledGridCol>
      </StyledGridRow>
    </Form>
  );
}